import { CNS_API_BASE } from "@/config";
import { getBrowserIanaTimeZone } from "@/lib/browser-timezone";
import {
  CNSProject,
  CNSCreateProject,
  CNSProjectPage,
  CNSProjectAnalytics,
  CNSProjectNextStepsView,
  CNSProjectNextStepsScope,
  CNSProjectNextStepEntityType,
  CNSConversationResolution,
  CNSConversation,
  JsonPatchOperation,
} from "@/models/cns";
import { throwOnProblemResponse } from "@/types/problem-detail";

const PROJECTS_URL = `${CNS_API_BASE}/projects`;

// Fetch active projects for a workspace (workspaceId is workspace public_id e.g. wks_xxx). App is workspace-aware; no GET /projects.
// When workspaceId is null/undefined, returns [] without calling the API.
export async function fetchProjectsByWorkspace(workspaceId: string | null | undefined): Promise<CNSProject[]> {
  if (workspaceId == null) return [];
  const res = await fetch(`${CNS_API_BASE}/workspaces/${workspaceId}/projects`, { credentials: "include" });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch workspace projects");
  const data: CNSProjectPage = await res.json();
  return data.content || [];
}

// Create a new project in the given workspace (workspace public ID required)
export async function createProject(title: string, workspaceId: string): Promise<CNSProject> {
  const body: CNSCreateProject = { title, workspaceId };
  const res = await fetch(PROJECTS_URL, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to create project");
  return res.json();
}

/**
 * Project analytics (counts + median learning cycle in whole calendar days).
 * Sends the browser IANA time zone so the median uses local calendar boundaries; storage remains UTC on the server.
 */
export async function fetchProjectAnalytics(projectId: string): Promise<CNSProjectAnalytics> {
  const params = new URLSearchParams({ timezone: getBrowserIanaTimeZone() });
  const res = await fetch(`${PROJECTS_URL}/${projectId}/analytics?${params.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch project analytics");
  return res.json();
}

/**
 * State-derived next-step catalog for a project.
 * Backend ordering (OVERDUE > ATTENTION > INFO, newest first within each tier) is preserved;
 * the response is capped at 50 items server-side and {@code totalCount} reflects the full count.
 *
 * @param projectId project public id (e.g. prj_xxx)
 * @param scope     "mine" filters to items the current user can act on; "all" (default) returns every open item
 */
export async function fetchProjectNextSteps(
  projectId: string,
  scope: CNSProjectNextStepsScope = "all",
): Promise<CNSProjectNextStepsView> {
  const params = new URLSearchParams({ scope });
  const res = await fetch(`${PROJECTS_URL}/${projectId}/next-steps?${params.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch project next steps");
  return res.json();
}

/**
 * Resolves the owning conversation for a project-scoped EMS entity.
 * Uses the same lineage rules as next-steps enrichment (primary facts, else latest updated_at).
 *
 * @param projectId project public id
 * @param entityType hypothesis | experiment | learning | insight | conversation
 * @param entityPublicId entity public id
 * @returns conversation public id, or null when orphaned / not in project
 */
export async function fetchProjectConversationResolution(
  projectId: string,
  entityType: CNSProjectNextStepEntityType,
  entityPublicId: string,
): Promise<string | null> {
  const params = new URLSearchParams({
    entityType,
    entityPublicId,
  });
  const res = await fetch(
    `${PROJECTS_URL}/${projectId}/conversation-resolution?${params.toString()}`,
    { credentials: "include" },
  );
  if (!res.ok) await throwOnProblemResponse(res, "Failed to resolve conversation for entity");
  const data: CNSConversationResolution = await res.json();
  return data.conversationPublicId ?? null;
}

// Fetch conversations for a project
export async function fetchProjectConversations(
  projectId: string,
): Promise<CNSConversation[]> {
  const res = await fetch(`${PROJECTS_URL}/${projectId}/conversations`, { credentials: "include" });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch project conversations");
  const data = await res.json();
  return data.content || [];
}

// Rename a project using PATCH (JSON Patch)
export async function renameProject(projectId: string, newTitle: string): Promise<CNSProject> {
  const patchBody: JsonPatchOperation[] = [
    {
      op: "replace",
      path: "/title",
      value: newTitle
    }
  ];
  const res = await fetch(`${PROJECTS_URL}/${projectId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json-patch+json" },
    credentials: "include",
    body: JSON.stringify(patchBody),
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to rename project");
  return res.json();
}

// Delete a project (soft delete)
export async function deleteProject(projectId: string): Promise<void> {
  const res = await fetch(`${PROJECTS_URL}/${projectId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to delete project");
}
